import React, {useEffect, useState} from 'react';
import {TableCell} from '@bosch/react-frok';
import {ItemThumbPlaceholder} from '../../ItemPlaceholder/ItemThumbPlaceholder';
import {executeAction, formatDate, formatFiletype, formatSize} from '../../../utils';
import {t} from 'i18next';
import {Label} from '../../../models/Label';
import {getLabel, isFolder} from '../../../utils/StorageObjectUtils';
import {ActionsCell, ItemEntryRow, Title, TitleCell} from './ItemTableRow.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {ItemViewProps} from '../ItemViewProps';
import {
    ITEMS_HEADER_LAYOUT_TABLE_BODY,
    ITEMS_HEADER_MOBILE_LAYOUT_TABLE_BODY,
    ITEMS_VIEW_COMMAND_ACTION,
    ITEMS_VIEW_DATE_CREATED,
    ITEMS_VIEW_FILE_TYPE,
    ITEMS_VIEW_MOBILE_DATE_CREATED,
    ITEMS_VIEW_MOBILE_FILE_TYPE,
    ITEMS_VIEW_MOBILE_NAME,
    ITEMS_VIEW_MOBILE_PUBLIC_ACCESS,
    ITEMS_VIEW_MOBILE_THUMBNAIL,
    ITEMS_VIEW_MOBILE_TOTAL_SIZE,
    ITEMS_VIEW_NAME,
    ITEMS_VIEW_PUBLIC_ACCESS,
    ITEMS_VIEW_THUMBNAIL,
    ITEMS_VIEW_TOTAL_SIZE
} from '../../../init/ui_component_constants';
import {CommandActionsBar} from '../CommandActionsBar/CommandActionsBar';

/**
 * @name ItemTableRow
 * @description
 * Row representation of an item (folder/file) for the ItemTable component
 */
export const ItemTableRow: React.VFC<ItemViewProps> = (props) => {
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(props.item.objectType, props.item.templateId));
    const isFolderItem = isFolder(props.item.objectType);
    const [label, setLabel] = useState<Label>(getLabel(props.defaultLocale, props.item.labels));
    if (!(isVisibleComponent(ITEMS_HEADER_LAYOUT_TABLE_BODY, templatePropertySettings)
        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_LAYOUT_TABLE_BODY, templatePropertySettings))))) {
        return null;
    }

    useEffect(() => {
        setLabel(getLabel(props.defaultLocale, props.item.labels));
    }, [props.defaultLocale, props.item.labels]);

    return (
        <ItemEntryRow id={generateIdentifier()}
                      onClick={(e) => executeAction(e, props.onClick)}>
            <TableCell id={generateIdentifier()}
                       hidden={!(isVisibleComponent(ITEMS_VIEW_THUMBNAIL, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_THUMBNAIL, templatePropertySettings))))}>
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings))) &&
                    <ItemThumbPlaceholder
                        contentType={isFolderItem ? 'folder' : props.item.metadata.contentType ?? null}
                        viewType={LayoutViewType.TABLE}/>
                }
            </TableCell>
            <TitleCell id={generateIdentifier()}
                       hidden={!(isVisibleComponent(ITEMS_VIEW_NAME, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_NAME, templatePropertySettings))))}>
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)) &&
                    <Title id={generateIdentifier()} className='highlight'>
                        {/*{(props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_LAYOUT_TABLE_ROW_MERGE_FLEXIBLE_DATA_INTO_NAME, templatePropertySettings)) &&*/}
                        {/*    props.item.metadata.flexibleData?*/}
                        {/*    .filter(convertToStringArray(getDefaultValueOfComponent(ITEMS_HEADER_MOBILE_LAYOUT, templatePropertySettings)).includes(flexibleDataKey))*/}
                        {/*    .map(flexibleDataItem => <>convertToStringArray(''.concat(flexibleDataItem.value).concat(',').concat(flexibleDataItem.values))<br/><div id={generateIdentifier()}/>)}*/}
                        {label.name}
                    </Title>
                }
            </TitleCell>
            <TableCell id={generateIdentifier()}
                       hidden={!(isVisibleComponent(ITEMS_VIEW_FILE_TYPE, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_FILE_TYPE, templatePropertySettings))))}>
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings)) &&
                    formatFiletype(isFolderItem ? 'folder' : props.item.metadata.contentType ?? null)
                }
            </TableCell>
            <TableCell id={generateIdentifier()}
                       hidden={!(isVisibleComponent(ITEMS_VIEW_DATE_CREATED, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DATE_CREATED, templatePropertySettings))))}>
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings)) &&
                    formatDate(props.language, props.item.metadata.dateCreated)
                }
            </TableCell>
            <TableCell id={generateIdentifier()}
                       hidden={!(isVisibleComponent(ITEMS_VIEW_TOTAL_SIZE, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TOTAL_SIZE, templatePropertySettings))))}
                       align='right'>
                {isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings) &&
                    formatSize(props.item.totalSize)
                }
            </TableCell>
            <TableCell id={generateIdentifier()}
                       hidden={!(props.editMode && (isVisibleComponent(ITEMS_VIEW_PUBLIC_ACCESS, templatePropertySettings)
                           && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_PUBLIC_ACCESS, templatePropertySettings)))))}>
                {(props.editMode && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings)) ?
                    (props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')) : ''}
            </TableCell>
            <ActionsCell id={generateIdentifier()}
                         hidden={!isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION, templatePropertySettings)}>
                {isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION, templatePropertySettings) &&
                    <CommandActionsBar templatePropertySettings={templatePropertySettings}
                                       isMobileView={props.isMobileView}
                                       layoutView={props.layoutView}
                                       item={props.item}
                                       editMode={props.editMode}
                                       publicOnlyAccess={props.publicOnlyAccess}
                                       isFavorite={props.isFavorite}
                                       onDetails={props.onDetails}
                                       onEdit={props.onEdit}
                                       onDelete={props.onDelete}
                                       onDownload={props.onDownload}
                                       onFavorite={props.onFavorite}
                                       onPreview={props.onPreview}
                                       onCopyUrl={props.onCopyUrl}/>
                }
            </ActionsCell>
        </ItemEntryRow>
    );
};
